import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { fmtWhen } from "../labels";

/* Cached AI text for the active period: kind = "conclusions" | "written". */
export function useReportAi(kind, queryParams, enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const load = useCallback(async (force = false) => {
    setLoading(true); setError(null);
    try { const { data: d } = await api.get("/kimkim/report/ai", { params: { ...queryParams, kind, force: force ? 1 : 0 } }); setData(d); }
    catch (e) { const d = e?.response?.data?.detail; setError(typeof d === "string" ? d : "No se pudo redactar con la IA"); }
    finally { setLoading(false); }
  }, [kind, queryParams]);
  useEffect(() => { setData(null); if (enabled) load(); }, [load, enabled]);
  return { data, loading, error, regenerate: () => load(true) };
}

export function RegenerateButton({ onClick, loading, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={loading} data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs border border-clay-300 px-3 py-1.5 hover:border-clay-900 disabled:opacity-50">
      <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> Regenerar
    </button>
  );
}

export function Conclusions({ queryParams, compact = false }) {
  const { data, loading, error, regenerate } = useReportAi("conclusions", queryParams);
  return (
    <section className={`bg-clay-900 text-clay-50 ${compact ? "px-5 py-5" : "px-6 py-6 md:px-8 md:py-7"}`} data-testid="kk-r-conclusions">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="smallcaps text-clay-300 flex items-center gap-1.5"><Sparkles size={12}/> Redactado por la IA a partir de las cifras</div>
          <h2 className="font-serif text-xl md:text-2xl leading-tight mt-1">Conclusiones del periodo</h2>
        </div>
        {!compact && <RegenerateButton onClick={regenerate} loading={loading} testid="kk-r-conclusions-regen"/>}
      </div>
      {error && <div className="text-sm text-terracotta" data-testid="kk-r-conclusions-error">{error} <button onClick={() => regenerate()} className="underline ml-1">Reintentar</button></div>}
      {!data && !error && <div className="text-sm text-clay-300" data-testid="kk-r-conclusions-loading">Redactando…</div>}
      {data && (
        <div className={`grid ${compact ? "" : "lg:grid-cols-[3fr_2fr]"} gap-8 ${loading ? "opacity-60" : ""}`}>
          <ol className="space-y-2 text-sm leading-relaxed list-decimal pl-5" data-testid="kk-r-conclusions-list">
            {(data.conclusiones || []).slice(0, compact ? 4 : 8).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          <div>
            <div className="smallcaps text-clay-300 mb-2">Tres acciones</div>
            <ul className="space-y-2 text-sm" data-testid="kk-r-conclusions-actions">
              {(data.acciones || []).slice(0, 3).map((s, i) => <li key={i} className="border-l-2 border-terracotta pl-3">{s}</li>)}
            </ul>
            {!compact && <div className="text-[11px] text-clay-400 mt-4">Generado {fmtWhen(data.generated_at)} · {data.model}</div>}
          </div>
        </div>
      )}
    </section>
  );
}
